import { Analytics } from '@cobliteam/analytics';
import type { Vehicle } from '@cobliteam/common-types';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { Check } from 'lucide-react';
import { useState } from 'react';

interface VehicleStepBodyProps {
  fleetId?: string;
  /** Called after the vehicle is created, to advance to the next step. */
  onCompleted?: () => void;
}

/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/** Inline "register a vehicle" — license plate only; details can be filled later on the vehicles page. */
const VehicleStepBody = ({ fleetId, onCompleted }: VehicleStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [plate, setPlate] = useState('');
  const { mutateAsync: createVehicle, isPending, isSuccess } = useFuscaVehicle().createNewVehicle;

  const handleCreate = async () => {
    const trimmed = plate.trim().toUpperCase();
    if (!trimmed) return;
    // createNewVehicle reads only the fields it needs (licensePlate); the rest stay undefined.
    await createVehicle({ licensePlate: trimmed } as Vehicle);
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'vehicles' });
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (isSuccess) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.vehicle_created')}
      </Typography>
    );
  }

  return (
    <div className='flex items-end gap-2'>
      <Input className='flex-1' value={plate} onChange={(event) => setPlate(event.target.value)} placeholder={t('inline.vehicle_plate')} />
      <Button loading={isPending} disabled={!plate.trim()} onClick={handleCreate}>
        {t('inline.create_vehicle')}
      </Button>
    </div>
  );
};

export default VehicleStepBody;
